// Cihaz drawer'ı (admin): salt-okunur bilgi listesi + durum değiştirme formu.
// Satır tıklanınca açılır; "İşlemler" menüsü yerine aksiyonlar drawer'ın altında
// (docs/design-principles.md §3). İki görünüm:
//   detay  → footer: "Sil" + "Durumu değiştir" (yalnız admin-devices:manage)
//   durum  → yeni durum çipleri (+ Hastada ise hasta seçimi), footer: "Geri" + "Kaydet"

import React, { useEffect, useMemo, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import {
  DevicesOther as DevicesIcon,
  DeleteOutline as DeleteIcon,
  SwapHoriz as SwapIcon,
} from "@mui/icons-material";
import { ActionButton, ChoiceChipGroup, DrawerFooter, DrawerHeader, FormAutocomplete } from "../../components/atoms";
import { DrawerField } from "../../components/molecules";
import { DetailList, StatusPill } from "../../components/organisms/ClinicRecordList";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";
import { STATUS_OPTIONS, STATUS_TONE } from "./constants";
import { typeLabelOf } from "./deviceListModel";

const STATUS_HINT = {
  Hastada: "Cihaz seçilen hastaya bağlanır; ölçümleri hastanın kaydına düşer.",
  Servis: "Cihaz bakım/onarımda. Hastaya bağlıysa bağ kaldırılır.",
  Depo: "Cihaz boşta, yeni bir hastaya verilmeyi bekliyor. Hastaya bağlıysa bağ kaldırılır.",
};

const patientLabel = (p) => (p ? `${p.name || ""} ${p.surname || ""}`.trim() : "");

export default function DeviceDetailDrawer({
  open,
  onClose,
  device,
  canManage,
  patients,
  onLoadPatients,
  onSubmitStatus,
  isSubmitting,
  onDelete,
}) {
  const { palette } = useClinicTheme();
  const [mode, setMode] = useState("detail");
  const [status, setStatus] = useState("");
  const [patient, setPatient] = useState(null);

  // Her açılışta detay görünümünden başla
  useEffect(() => {
    if (open) {
      setMode("detail");
      setStatus("");
      setPatient(null);
    }
  }, [open, device?._id]);

  const statusChoices = useMemo(
    () => STATUS_OPTIONS.filter((o) => o.value !== device?.statusName),
    [device?.statusName]
  );

  const startStatusChange = () => {
    setStatus(statusChoices[0]?.value || "");
    setPatient(null);
    setMode("status");
    onLoadPatients?.();
  };

  const canSave = Boolean(status) && (status !== "Hastada" || Boolean(patient));

  const submit = async () => {
    const ok = await onSubmitStatus?.(device, status, patient?._id);
    if (ok) setMode("detail");
  };

  const statusPill = (value) => (
    <StatusPill label={value || "—"} tone={STATUS_TONE[value]} palette={palette} />
  );

  return (
    <Drawer
      anchor="right"
      open={open && Boolean(device)}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 520 }, display: "flex", flexDirection: "column", bgcolor: COLORS.white },
      }}
    >
      {device && (
        <>
          <DrawerHeader
            icon={mode === "status" ? <SwapIcon /> : <DevicesIcon />}
            title={mode === "status" ? "Durumu değiştir" : typeLabelOf(device)}
            subtitle={device.serialNo}
            onClose={onClose}
          />

          {mode === "detail" ? (
            <>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
                <DetailList
                  items={[
                    { label: "Seri no", value: device.serialNo, mono: true },
                    { label: "MAC adresi", value: device.mac, mono: true },
                    { label: "Cihaz tipi", value: typeLabelOf(device) },
                    { label: "Model", value: device.modelLabel || device.modelId || null },
                    device.name ? { label: "Cihaz adı", value: device.name } : null,
                    device.sdk ? { label: "SDK", value: device.sdk } : null,
                    { label: "Durum", value: statusPill(device.statusName) },
                    { label: "Hasta", value: patientLabel(device.patient) || null },
                    device.patient?.email ? { label: "Hasta e-postası", value: device.patient.email } : null,
                    { label: "Hastaya bağlanma", value: device.boundAt ? fullDateTime(device.boundAt) : null },
                    { label: "Sisteme tanıtılma", value: device.provisionedAt ? fullDateTime(device.provisionedAt) : null },
                  ]}
                />
              </Box>

              {canManage ? (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: `1px solid ${COLORS.slate200}`,
                    display: "flex",
                    gap: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <ActionButton
                    variant="delete"
                    tone="outline"
                    label="Sil"
                    size="medium"
                    icon={<DeleteIcon />}
                    onClick={() => onDelete?.(device)}
                    sx={{ minHeight: 40, px: 2 }}
                  />
                  <ActionButton
                    variant="edit"
                    palette={palette}
                    label="Durumu değiştir"
                    size="medium"
                    icon={<SwapIcon />}
                    onClick={startStatusChange}
                    fullWidth
                    sx={{ minHeight: 40, px: 2 }}
                  />
                </Box>
              ) : (
                <DrawerFooter onCancel={onClose} cancelLabel="Kapat" fullWidth />
              )}
            </>
          ) : (
            <>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "grid", rowGap: 2.5 }}>
                  <DrawerField label="Mevcut durum">{statusPill(device.statusName)}</DrawerField>

                  <DrawerField label="Yeni durum *" hint={STATUS_HINT[status]}>
                    <Box sx={{ pt: 0.5 }}>
                      <ChoiceChipGroup
                        ariaLabel="Yeni durum"
                        options={statusChoices}
                        value={status}
                        onChange={(v) => {
                          setStatus(v);
                          if (v !== "Hastada") setPatient(null);
                        }}
                      />
                    </Box>
                  </DrawerField>

                  {status === "Hastada" && (
                    <DrawerField label="Hasta *">
                      <FormAutocomplete
                        variant="standard"
                        palette={palette}
                        options={patients || []}
                        value={patient}
                        onChange={(_, v) => setPatient(v)}
                        getOptionLabel={(p) => [patientLabel(p), p?.email].filter(Boolean).join(" · ")}
                        placeholder="Ad, soyad veya e-posta ile ara"
                        noOptionsText="Hasta bulunamadı"
                      />
                    </DrawerField>
                  )}

                  <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                    MAC <Box component="span" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace" }}>{device.mac}</Box>
                  </Typography>
                </Box>
              </Box>

              <DrawerFooter
                onCancel={() => setMode("detail")}
                cancelLabel="Geri"
                onSubmit={submit}
                submitLabel="Kaydet"
                loadingLabel="Kaydediliyor..."
                loading={isSubmitting}
                disabled={!canSave}
                fullWidth
              />
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
